import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import type { AssignmentStatus, OrientationVersion } from '@/generated/minetwin/types';

/**
 * Trainee-side data hook for Story 7e (Trainee Portal).
 *
 * Subscribes to the `my_assignments` view plus `my_assignment_progress` for
 * the trainee's current-step pointer. Phase 3 of the Workforce Alignment
 * Plan repointed assignments from `employee_id` to `email` (post-Phase-3
 * assignments carry both `email` and an optional `user_id` filled in by
 * `client_connected`'s drain). The view filter keys on email — empty for
 * anyone whose JWT email doesn't match any open assignment.
 *
 * Returns one entry per assignment with the progress row merged in.
 */
export type MyAssignmentRow = {
  assignmentId: bigint;
  /** The assignee's email — stable cross-state key. */
  email: string;
  /** Post-drain bind. `null` for assignments still keyed only on email. */
  userId: string | null;
  orientationId: string;
  projectId: string;
  orientationVersion: OrientationVersion;
  status: AssignmentStatus;
  assignedAt: number;
  startedAt: number | null;
  completedAt: number | null;
  cancelledAt: number | null;
  /** Current step pointer + position from my_assignment_progress (null if Not Started). */
  currentStepId: string | null;
  currentPosition: number;
  sessionId: bigint;
  progressUpdatedAt: number | null;
};

export function useMyAssignments(): {
  assignments: MyAssignmentRow[];
  isReady: boolean;
} {
  const [assignmentRows, assignmentsReady] = useTable(
    'minetwin',
    minetwinTables.my_assignments,
  );
  const [progressRows, progressReady] = useTable(
    'minetwin',
    minetwinTables.my_assignment_progress,
  );

  const assignments = useMemo<MyAssignmentRow[]>(() => {
    const progressByAssignment = new Map<
      bigint,
      (typeof progressRows)[number]
    >();
    for (const p of progressRows) progressByAssignment.set(p.assignmentId, p);

    return assignmentRows
      .map((a) => {
        const p = progressByAssignment.get(a.assignmentId);
        return {
          assignmentId: a.assignmentId,
          email: a.email,
          userId: a.userId ?? null,
          orientationId: a.orientationId,
          projectId: a.projectId,
          orientationVersion: a.orientationVersion,
          status: a.status,
          assignedAt: toMillis(a.assignedAt),
          startedAt: a.startedAt ? toMillis(a.startedAt) : null,
          completedAt: a.completedAt ? toMillis(a.completedAt) : null,
          cancelledAt: a.cancelledAt ? toMillis(a.cancelledAt) : null,
          currentStepId: p?.currentStepId ?? null,
          currentPosition: p?.currentPosition ?? 0,
          sessionId: p?.sessionId ?? 0n,
          progressUpdatedAt: p ? toMillis(p.updatedAt) : null,
        };
      })
      .sort((a, b) => b.assignedAt - a.assignedAt);
  }, [assignmentRows, progressRows]);

  return { assignments, isReady: assignmentsReady && progressReady };
}

function toMillis(t: unknown): number {
  if (typeof t === 'number') return t;
  if (t instanceof Date) return t.getTime();
  if (t && typeof t === 'object') {
    const anyT = t as { micros?: bigint; getTime?: () => number };
    if (typeof anyT.getTime === 'function') return anyT.getTime();
    if (typeof anyT.micros === 'bigint') return Number(anyT.micros / 1000n);
  }
  return 0;
}
